import { ChangeDetectionStrategy, Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { NgbActiveModal, NgbDatepicker, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-date-picker-modal',
  imports: [NgbDatepicker],
  templateUrl: './date-picker-modal.component.html',
  styleUrl: './date-picker-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerModalComponent implements OnInit {
  readonly activeModal = inject(NgbActiveModal);

  @Input() title = 'Select a date';
  @Input() confirmText = 'Confirm';
  @Input() initialDate?: Date;
  @Input() minDate?: Date;

  protected readonly _selected = signal<Date>(this.startOfToday());

  protected readonly selectedNgbDate = computed<NgbDateStruct>(() => {
    const d = this._selected();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  });

  protected readonly minNgbDate = computed<NgbDateStruct>(() => {
    const d = this.minDate ?? this.startOfToday();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  });

  protected readonly selectedLabel = computed(() => {
    const d = this._selected();
    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
    const day = d.getDate();
    const month = d.toLocaleDateString('en-US', { month: 'long' });
    const year = d.getFullYear();
    return `${weekday}, ${day}${this.ordinalSuffix(day)} ${month} ${year}`;
  });

  ngOnInit(): void {
    const initial = this.initialDate;
    if (initial && !Number.isNaN(initial.getTime())) {
      const min = this.minDate ?? this.startOfToday();
      this._selected.set(initial.getTime() < min.getTime() ? min : this.stripTime(initial));
    }
  }

  protected onDateSelect(date: NgbDateStruct): void {
    this._selected.set(new Date(date.year, date.month - 1, date.day));
  }

  protected confirm(): void {
    this.activeModal.close(this._selected());
  }

  protected cancel(): void {
    this.activeModal.dismiss();
  }

  private startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private stripTime(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private ordinalSuffix(n: number): string {
    const tens = n % 100;
    if (tens >= 11 && tens <= 13) return 'th';
    switch (n % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }
}
